import {BrowserProvider, Contract, parseUnits, getAddress, formatUnits, id, AbiCoder, JsonRpcProvider} from 'ethers'
import {
	USDT_ADDRESS,
	ERC20_ABI,
	ERC20,
	isProd,
	isMainnet,
	TEST_USDT_ADDRESS,
	NFT_ABI,
	INTERACTOR_ABI,
	ERC20_INTERFACE,
	NFT_INTERFACE,
	INTERACTOR_INTERFACE, LP_ABI,
	LP_INTERFACE,
	Interactor,
	FAANFT,
	FireAntCoin,
	FAALiquidPool
} from '@/utils/constants'


let provider, browserProvider, rpcProvider

const abiCoder = AbiCoder.defaultAbiCoder();

const rpcUrlTestnet = 'https://data-seed-prebsc-1-s1.binance.org:8545'
// const rpcUrlTestnet = 'wss://few-fragrant-feather.bsc-testnet.quiknode.pro/808238e5e9b9c0b5a64bba4fb77bd89fb050111b/'
// const rpcUrlMainnet = 'https://bsc-dataseed1.binance.org'
const rpcUrlMainnet = 'https://fac-s1.lihmxssuqleitqcxpssa.uk:1818/bsc'

async function pickEvmProvider({timeout = 3000} = {}) {
	// 等待 window.ethereum 注入
	const eth = await waitForEthereum(timeout);

	// 同步兼容：若仍没有注入，尝试 TP 专用兜底对象
	let providers = [];
	if (Array.isArray(eth?.providers) && eth.providers.length) {
		providers = eth.providers.slice();
	} else if (eth) {
		providers = [eth];
	}

	// 某些 TP 环境会把 provider 暴露在其他命名空间
	if (!providers.length) {
		if (window.tokenpocket?.ethereum) providers.push(window.tokenpocket.ethereum);
		if (window.tpEvm) providers.push(window.tpEvm);
	}

	if (!providers.length) return null;

	// 优先序：可按你的需求调整
	const matchers = [
		(p) => isTokenPocketProvider(p),        // 如果你更想优先 TP，放在最前面
		(p) => p?.isMetaMask,
		(p) => p?.isOkxWallet || p?.isOKXWallet || p?.isOKExWallet,
		(p) => p?.isCoinbaseWallet,
	];

	for (const m of matchers) {
		const found = providers.find(m);
		if (found) return found;
	}
	return providers[0] || null;
}

async function waitForEthereum(timeout = 3000, retryCount = 3) {
	// 如果已经存在且可用，直接返回
	if (window.ethereum?.isConnected?.()) {
		return window.ethereum;
	}

	if (window.ethereum) {
		try {
			// 清理可能存在的旧监听器
			window.ethereum.removeAllListeners?.();
			return window.ethereum;
		} catch (error) {
			console.warn('清理 ethereum 监听器失败:', error);
		}
	}

	return new Promise((resolve, reject) => {
		let resolved = false;
		let currentRetry = 0;

		const done = (val, error = null) => {
			if (!resolved) {
				resolved = true;

				if (error) {
					reject(error);
				} else if (val) {
					// 验证返回的 provider 是否有效
					try {
						if (val.request) {
							resolve(val);
						} else {
							reject(new Error('Invalid ethereum provider: missing request method'));
						}
					} catch (err) {
						reject(new Error('Invalid ethereum provider: ' + err.message));
					}
				} else {
					reject(new Error('Ethereum provider not found'));
				}
			}
		};

		const attemptConnection = () => {
			currentRetry++;

			// EIP-1193 建议事件
			window.addEventListener('ethereum#initialized', () => {
				if (window.ethereum) {
					console.log('✓ Ethereum 通过事件初始化');
					done(window.ethereum);
				}
			}, {once: true});

			// 兜底轮询，避免部分环境不派发事件
			let tries = 0;
			const maxTries = Math.ceil(timeout / 100);

			const iv = setInterval(() => {
				tries++;

				if (window.ethereum) {
					clearInterval(iv);
					console.log(`✓ Ethereum 通过轮询找到 (尝试 ${tries}/${maxTries})`);

					// 验证 provider 状态
					try {
						if (window.ethereum.request) {
							done(window.ethereum);
						} else {
							console.warn('Ethereum provider 缺少 request 方法');
							if (currentRetry < retryCount) {
								console.log(`重试 ${currentRetry + 1}/${retryCount}...`);
								setTimeout(attemptConnection, 500);
							} else {
								done(null, new Error('Invalid ethereum provider after retries'));
							}
						}
					} catch (error) {
						console.error('验证 provider 时出错:', error);
						if (currentRetry < retryCount) {
							console.log(`重试 ${currentRetry + 1}/${retryCount}...`);
							setTimeout(attemptConnection, 500);
						} else {
							done(null, error);
						}
					}
				} else if (tries >= maxTries) {
					clearInterval(iv);
					console.warn(`轮询超时 (${tries} 次尝试)`);

					if (currentRetry < retryCount) {
						console.log(`重试 ${currentRetry + 1}/${retryCount}...`);
						setTimeout(attemptConnection, 500);
					} else {
						done(null, new Error('Ethereum provider not found after retries'));
					}
				}
			}, 100);

			// 超时保护
			setTimeout(() => {
				clearInterval(iv);

				if (!resolved) {
					if (window.ethereum) {
						console.log('✓ 超时但找到 Ethereum');
						done(window.ethereum);
					} else if (currentRetry < retryCount) {
						console.log(`超时，重试 ${currentRetry + 1}/${retryCount}...`);
						setTimeout(attemptConnection, 500);
					} else {
						done(null, new Error(`Timeout waiting for ethereum provider (${timeout}ms)`));
					}
				}
			}, timeout);
		};

		// 开始第一次连接尝试
		attemptConnection();
	});
}

function isTokenPocketProvider(p) {
	return !!(
		p &&
		(p.isTokenPocket ||
			p.isTP ||
			p?.walletName === 'TokenPocket' ||
			p?.__TP ||
			p?.providerName === 'TokenPocket')
	);
}


async function connectDirect(isMainnet = true) {

	try {
		console.log(`isMainnet: ${isMainnet}`)
		provider = await pickEvmProvider();
		browserProvider = new BrowserProvider(provider)
		rpcProvider = new JsonRpcProvider(isMainnet === true ? rpcUrlMainnet : rpcUrlTestnet)
		if (!provider) throw new Error('No EVM provider found');
		const accounts = await provider.request({method: 'eth_requestAccounts'});
		let chainId = await provider.request({method: 'eth_chainId'});

		console.log('Connected via', provider.isMetaMask ? 'MetaMask' : 'Other', accounts[0], chainId);

		// BSC 主链 chainId: 0x38 (56), BSC 测试链 chainId: 0x61 (97)
		const BSC_MAINNET = '0x38';
		const BSC_TESTNET = '0x61';

		if (isMainnet !== true && chainId === BSC_MAINNET) {
			try {
				// 尝试切换到 BSC 测试链
				await provider.request({
					method: 'wallet_switchEthereumChain',
					params: [{chainId: BSC_TESTNET}]
				});

				// 重新获取 chainId 确认切换成功
				chainId = await provider.request({method: 'eth_chainId'});
				console.log('已切换到 BSC 测试链:', chainId);

			} catch (switchError) {
				// 如果测试链不存在，添加测试链
				if (switchError.code === 4902) {
					await provider.request({
						method: 'wallet_addEthereumChain',
						params: [{
							chainId: BSC_TESTNET,
							chainName: 'BSC Testnet',
							nativeCurrency: {
								name: 'BNB',
								symbol: 'tBNB',
								decimals: 18
							},
							rpcUrls: ['bsc-testnet-rpc.publicnode.com'],
							blockExplorerUrls: ['https://testnet.bscscan.com/']
						}]
					});

					// 添加后重新获取 chainId
					chainId = await provider.request({method: 'eth_chainId'});
					console.log('已添加并切换到 BSC 测试链:', chainId);
				} else {
					throw switchError;
				}
			}
		}

		return {provider: provider, account: accounts[0], chainId};

	} catch (e) {
		console.error('direct connect error:', e, e?.code, e?.message);
		throw e;
	}
}

// 将十进制字符串按 decimals 转成 uint256 的十六进制字符串（不带0x）
function parseUnitsToHex(amountStr, decimals) {
	// 纯前端实现，避免依赖库
	// 将 "123.456" 转为 BigInt 以 decimals 精度
	if (typeof amountStr !== "string") amountStr = String(amountStr);
	const [intPart, fracPartRaw = ""] = amountStr.split(".");
	const fracPart = fracPartRaw.slice(0, decimals); // 截断多余小数
	const fracPadded = fracPart.padEnd(decimals, "0");
	const digits = (intPart || "0") + (decimals ? fracPadded : "");
	// 去除前导零
	const normalized = digits.replace(/^0+/, "") || "0";
	const bi = BigInt(normalized);
	return bi.toString(16); // 不带0x
}

// 发起 USDT 转账/*BSC Mainnet 56，Testnet = 0x61*/
async function sendUsdt({from, to, amountStr,}) {
	if (!provider) return -4;
	const expectedChainId = isMainnet === true ? "0x38" : "0x61"
	// 安全的 0x 十六進位標準化
	const toHex = (v) => {
		try {
			if (typeof v === "string") {
				if (v.startsWith("0x")) return v.toLowerCase();
				// 嘗試將十進制字串轉為 hex
				return "0x" + BigInt(v).toString(16);
			}
			if (typeof v === "number") return "0x" + BigInt(v).toString(16);
			if (typeof v === "bigint") return "0x" + v.toString(16);
			if (v && typeof v === "object") {
				// ethers BigNumber
				if (typeof v.toHexString === "function") return v.toHexString().toLowerCase();
				// Uint8Array / Buffer
				if (ArrayBuffer.isView(v)) {
					return "0x" + [...v].map((b) => b.toString(16).padStart(2, "0")).join("");
				}
			}
		} catch (e) {
			// fallthrough
		}
		throw new Error("toHex: unsupported value " + String(v));
	};

	// 統一識別“用戶取消”
	const isUserRejectedError = (err) => {
		if (!err) return false;
		const code = err.code;
		const msg = String(err.message || err || "").toLowerCase();
		if (code === 4001) return true;
		if (code === "ACTION_REJECTED") return true;
		if (code === 5001) return true;
		if (
			/user (rejected|denied)/i.test(msg) ||
			/rejected by user/i.test(msg) ||
			msg.includes("action rejected") ||
			msg.includes("request rejected") ||
			msg.includes("denied transaction signature") ||
			msg.includes("cancelled") ||
			msg.includes("canceled") ||
			msg.includes("取消") ||
			msg.includes("拒绝")
		) {
			return true;
		}
		return false;
	};

	const fromAddr = from;
	const toAddr = to;

	try {
		console.debug("[sendUsdt] start", {fromAddr, toAddr, expectedChainId, amountStr});

		// 1) 取得帳號
		const [activeAccount] = await provider.request({method: "eth_requestAccounts"});
		console.debug("[sendUsdt] activeAccount =", activeAccount);
		if (!activeAccount) throw new Error("No account authorized");
		if (fromAddr && activeAccount.toLowerCase() !== fromAddr.toLowerCase()) {
			throw new Error(`Active account ${activeAccount} does not match from ${fromAddr}`);
		}

		// 2) 檢查/切換鏈
		const chainId = await provider.request({method: "eth_chainId"});
		console.debug("[sendUsdt] current chainId =", chainId);
		if (chainId !== expectedChainId) {
			console.debug("[sendUsdt] switching chain to", expectedChainId);
			try {
				await provider.request({
					method: "wallet_switchEthereumChain",
					params: [{chainId: expectedChainId}],
				});
			} catch (switchErr) {
				console.error("[sendUsdt] switch chain error:", switchErr);
				if (isUserRejectedError(switchErr)) return -2;
				throw switchErr;
			}
		}

		const decimalsHex = await provider.request({
			method: "eth_call",
			params: [
				{to: isMainnet === true ? USDT_ADDRESS : TEST_USDT_ADDRESS, data: ERC20.selectors.decimals},
				"latest",
			],
		});
		console.debug("[sendUsdt] decimalsHex =", decimalsHex);
		const decimals = Number(ERC20.parseUint256(decimalsHex));
		console.debug("[sendUsdt] decimals =", decimals);

		const balanceData = ERC20.hex(
			ERC20.selectors.balanceOf,
			ERC20.addrTo32(activeAccount)
		);
		// console.debug("[sendUsdt] balanceOf data =", balanceData);
		const balanceHex = await provider.request({
			method: "eth_call",
			params: [{to: isMainnet === true ? USDT_ADDRESS : TEST_USDT_ADDRESS, data: balanceData}, "latest"],
		});
		console.debug("[sendUsdt] balanceHex =", balanceHex);
		const balance = ERC20.parseUint256(balanceHex);
		console.debug("[sendUsdt] balance (BI) =", balance.toString());

		// 5) 轉帳數額
		const amountHexNo0x = parseUnitsToHex(amountStr, decimals);
		console.debug("[sendUsdt] amountHexNo0x =", amountHexNo0x);
		const amountBI = BigInt("0x" + (amountHexNo0x || "0"));
		console.debug("[sendUsdt] amountBI =", amountBI.toString());
		if (balance < amountBI) {
			console.warn("[sendUsdt] insufficient balance", {
				balance: balance.toString(),
				need: amountBI.toString(),
			});
			return -1;
		}

		// 6) transfer data
		const transferData = ERC20.hex(
			ERC20.selectors.transfer,
			ERC20.addrTo32(toAddr),
			ERC20.uintTo32(amountHexNo0x)
		);
		console.debug("[sendUsdt] transferData =", transferData, "len=", transferData?.length);

		// 7) 預估 gas
		const gasEstimateRaw = await provider.request({
			method: "eth_estimateGas",
			params: [{
				from: activeAccount,
				to: isMainnet === true ? USDT_ADDRESS : TEST_USDT_ADDRESS,
				data: transferData
			}],
		});
		const gasEstimateHex = toHex(gasEstimateRaw);
		console.debug("[sendUsdt] gasEstimate raw=", gasEstimateRaw, "hex=", gasEstimateHex);

		// 8) 費用
		let maxFeePerGas, maxPriorityFeePerGas, gasPrice;
		let feeHistory;
		try {
			feeHistory = await provider.request({
				method: "eth_feeHistory",
				params: ["0x5", "latest", []],
			});
		} catch (e) {
			feeHistory = null;
		}
		console.debug("[sendUsdt] feeHistory =", feeHistory);

		if (feeHistory && Array.isArray(feeHistory.baseFeePerGas) && feeHistory.baseFeePerGas.length) {
			const lastBaseHexLike = feeHistory.baseFeePerGas[feeHistory.baseFeePerGas.length - 1];
			const lastBase = BigInt(toHex(lastBaseHexLike));
			const priority = 1_000_000_000n; // 1 gwei
			const maxFee = (lastBase * 12n) / 10n + priority;
			maxFeePerGas = toHex(maxFee);
			maxPriorityFeePerGas = toHex(priority);
			console.debug("[sendUsdt] EIP-1559 fees", {
				lastBase: lastBase.toString(),
				maxFeePerGas,
				maxPriorityFeePerGas
			});
		} else {
			const gpRaw = await provider.request({method: "eth_gasPrice"});
			gasPrice = toHex(gpRaw);
			console.debug("[sendUsdt] legacy gasPrice raw=", gpRaw, "hex=", gasPrice);
		}

		// 9) nonce
		const nonceRaw = await provider.request({
			method: "eth_getTransactionCount",
			params: [activeAccount, "pending"],
		});
		const nonceHex = toHex(nonceRaw);
		console.debug("[sendUsdt] nonce raw=", nonceRaw, "hex=", nonceHex);

		// 10) 準備 txParams
		const txParams = {
			from: activeAccount,
			to: isMainnet === true ? USDT_ADDRESS : TEST_USDT_ADDRESS,
			data: transferData,
			gas: gasEstimateHex,
			nonce: nonceHex,
		};
		if (gasPrice) {
			txParams.gasPrice = gasPrice;
		} else {
			txParams.maxFeePerGas = maxFeePerGas;
			txParams.maxPriorityFeePerGas = maxPriorityFeePerGas;
		}

		// 發送前全面檢查與打印
		console.debug("[sendUsdt] txParams =", JSON.parse(JSON.stringify(txParams)));
		Object.entries(txParams).forEach(([k, v]) => {
			console.debug(`[sendUsdt] txParams field: ${k} type=${typeof v} value=`, v);
			if (["gas", "gasPrice", "maxFeePerGas", "maxPriorityFeePerGas", "nonce"].includes(k) && typeof v === "string") {
				if (!v.startsWith("0x")) {
					console.warn(`[sendUsdt] WARNING: ${k} is not 0x-prefixed hex`, v);
				}
			}
		});

		// 11) 發送交易
		const txHash = await provider.request({
			method: "eth_sendTransaction",
			params: [txParams],
		});
		console.debug("[sendUsdt] txHash =", txHash);
		return txHash;
	} catch (err) {
		if (isUserRejectedError(err)) {
			console.warn("[sendUsdt] user rejected action:", err);
			return -2;
		}
		console.error("[sendUsdt] error:", err);
		try {
			// 某些錢包把原始 RPC 錯誤放在 error.data 或 error.originalError
			const raw = err?.data || err?.error || err?.originalError;
			if (raw) console.error("[sendUsdt] error.raw =", raw);
		} catch (_) {
		}
		console.error("[sendUsdt] stack:\n", err?.stack);
		return -3;
	}
}

function maskBscAddress(addr, maskStr = '***') {
	if (typeof addr !== 'string') {
		throw new Error('地址必须是字符串');
	}
	const trimmed = addr.trim();

	// 基础校验：0x开头，后面为40个十六进制字符（不区分大小写）
	const re = /^0x[0-9a-fA-F]{40}$/;
	if (!re.test(trimmed)) {
		// 如果不是标准长度，但你仍想处理，就尽量只保留首尾显示
		// 也可以直接抛错。这里选择宽松处理。
		const n = trimmed.length;
		if (n <= 8) return trimmed; // 太短，就不掩码
		return trimmed.slice(0, 4) + maskStr + trimmed.slice(-4);
	}

	// 对标准地址：显示前4位(含0x和后两位)，和后4位
	// 需求是“前四位和后四位”，通常理解为不算'0x'的情况下的前4位与后4位。
	// 若你希望把 0x 也计入，可把 start 改成 0、取前4。
	const startIndex = 2; // 跳过'0x'
	const prefix = trimmed.slice(0, 2) + trimmed.slice(startIndex, startIndex + 4); // 保留0x + 前4位
	const suffix = trimmed.slice(-4);
	return `${prefix}${maskStr}${suffix}`;
}

function isBscTxHashFormat(str) {
	if (typeof str !== "string") return false;
	// 0x + 64 hex chars
	return /^0x[a-fA-F0-9]{64}$/.test(str.trim());
}

function maskTxHash(hash, keep = 4) {
	if (typeof hash !== 'string') return String(hash);

	// 保留 0x 前缀（如果有）
	const hasPrefix = hash.startsWith('0x') || hash.startsWith('0X');
	const prefix = hasPrefix ? hash.slice(0, 2) : '';
	const body = hasPrefix ? hash.slice(2) : hash;

	// 基础校验：十六进制字符
	const hexRe = /^[0-9a-fA-F]+$/;
	if (!hexRe.test(body)) {
		// 不是标准十六进制，直接按普通字符串处理
		if (body.length <= keep * 2) return hash;
		return prefix + body.slice(0, keep) + '***' + body.slice(-keep);
	}

	// 长度不足时不遮蔽，避免信息丢失
	if (body.length <= keep * 2) {
		return prefix + body;
	}

	const masked = body.slice(0, keep) + '***' + body.slice(-keep);
	return prefix + masked;
}

function toChecksumAddress(address) {
	return getAddress(address); // 非法会抛错
}

function formatUnitsBI(valueBI, decimals) {
	const neg = valueBI < 0n;
	let value = neg ? -valueBI : valueBI;
	const base = 10n ** BigInt(decimals);
	const integer = value / base;
	const fraction = value % base;

	if (fraction === 0n) return (neg ? "-" : "") + integer.toString();

	const fracStr = fraction.toString().padStart(decimals, "0").replace(/0+$/, "");
	return (neg ? "-" : "") + integer.toString() + "." + (fracStr.length ? fracStr : "0");
}

function wait(ms) {
	return new Promise(resolve => setTimeout(resolve, ms));
}

async function getUsdtBalance(walletAddress) {

	const usdtAddress = isMainnet === true ? USDT_ADDRESS : TEST_USDT_ADDRESS;

	try {
		// 编码 balanceOf 调用
		const data = ERC20_INTERFACE.encodeFunctionData('balanceOf', [walletAddress]);

		// RPC 调用
		const result = await provider.request({
			method: 'eth_call',
			params: [{
				to: usdtAddress,
				data: data
			}, 'latest']
		});

		// 解码结果
		const balance = BigInt(result);

		console.log(`usdtBalance: ${balance}`);

		return balance;

	} catch (error) {
		console.error("getUsdtBalance 调用错误:", error);
		throw error;
	}
}

async function checkAllowance(wallet, spender) {
	const usdtAddress = isMainnet === true ? USDT_ADDRESS : TEST_USDT_ADDRESS;

	try {
		const contract = new Contract(usdtAddress, ERC20_ABI, rpcProvider);
		const usdtAllowance = await contract.allowance(wallet, spender);

		console.log(`usdtAllowance: ${usdtAllowance}`);

		return usdtAllowance;
	} catch (error) {
		console.error("checkAllowance 调用错误:", error);
		throw error;
	}

}

function toHex(v) {
	try {
		if (typeof v === "string") {
			if (v.startsWith("0x")) return v.toLowerCase();
			return "0x" + BigInt(v).toString(16);
		}
		if (typeof v === "number") return "0x" + BigInt(v).toString(16);
		if (typeof v === "bigint") return "0x" + v.toString(16);
		if (v && typeof v === "object") {
			if (typeof v.toHexString === "function") return v.toHexString().toLowerCase();
		}
	} catch (e) {
	}
	throw new Error("toHex: unsupported value " + String(v));
}

function isUserRejectedError(err) {
	if (!err) return false;
	const code = err.code;
	const msg = String(err.message || err || "").toLowerCase();
	if (code === 4001 || code === "ACTION_REJECTED" || code === 5001) return true;
	if (/user (rejected|denied)|cancelled|canceled|取消|拒绝/i.test(msg)) {
		return true;
	}
	return false;
}

async function prepareTransactionParams(from, to, data) {
	try {
		// 1. 估算 Gas（只添加 10% 缓冲，而不是 20%）
		const gasEstimateRaw = await provider.request({
			method: "eth_estimateGas",
			params: [{from, to, data}],
		});

		const gasEstimate = BigInt(gasEstimateRaw);
		// ✅ 减少缓冲到 10%（ethers.js 默认是 10-20%）
		const gasWithBuffer = (gasEstimate * 110n) / 100n;
		const gas = toHex(gasWithBuffer);

		console.debug("Gas 估算:", {
			原始: toHex(gasEstimate),
			带缓冲: gas,
			缓冲: "10%"
		});

		// 2. 获取 Nonce
		const nonceRaw = await provider.request({
			method: "eth_getTransactionCount",
			params: [from, "pending"],
		});
		const nonce = toHex(nonceRaw);

		// 3. 智能选择 Gas Price 策略
		let txParams = {
			from,
			to,
			data,
			gas,
			nonce
		};

		// 尝试使用 EIP-1559
		try {
			const feeData = await provider.request({
				method: "eth_feeHistory",
				params: ["0x4", "latest", [25, 50, 75]], // 获取最近4个区块的费用数据
			});

			if (feeData && Array.isArray(feeData.baseFeePerGas) && feeData.baseFeePerGas.length > 0) {
				// 获取最新的 baseFee
				const latestBaseFee = BigInt(feeData.baseFeePerGas[feeData.baseFeePerGas.length - 1]);

				// 计算中位数优先费用
				let medianPriorityFee = 1_000_000_000n; // 默认 1 gwei
				if (feeData.reward && feeData.reward.length > 0) {
					const rewards = feeData.reward
						.flat()
						.map(r => BigInt(r))
						.filter(r => r > 0n);

					if (rewards.length > 0) {
						rewards.sort((a, b) => a < b ? -1 : 1);
						medianPriorityFee = rewards[Math.floor(rewards.length / 2)];
					}
				}

				// ✅ 优化的 maxFeePerGas 计算
				// maxFeePerGas = baseFee + priorityFee（不需要 *1.2）
				const maxFeePerGas = latestBaseFee + medianPriorityFee;
				const maxPriorityFeePerGas = medianPriorityFee;

				txParams.maxFeePerGas = toHex(maxFeePerGas);
				txParams.maxPriorityFeePerGas = toHex(maxPriorityFeePerGas);

				console.debug("EIP-1559 费用:", {
					baseFeePerGas: toHex(latestBaseFee),
					maxPriorityFeePerGas: toHex(maxPriorityFeePerGas),
					maxFeePerGas: toHex(maxFeePerGas),
					priorityFeeGwei: Number(medianPriorityFee) / 1e9
				});

				return txParams;
			}
		} catch (e) {
			console.debug("EIP-1559 不可用，使用 legacy gas price:", e.message);
		}

		// 回退到 legacy gasPrice
		const gasPriceRaw = await provider.request({
			method: "eth_gasPrice"
		});

		// ✅ 不需要额外增加 gasPrice
		txParams.gasPrice = toHex(gasPriceRaw);

		console.debug("Legacy Gas Price:", txParams.gasPrice);

		return txParams;

	} catch (error) {
		console.error("准备交易参数失败:", error);
		throw error;
	}
}

async function verifyChainId(expectedChainId) {
	const chainId = await provider.request({method: "eth_chainId"});
	console.debug("[approveUSDT] current chainId =", chainId);
	if (chainId !== expectedChainId) {
		console.debug("[approveUSDT] switching chain to", expectedChainId);
		try {
			await provider.request({
				method: "wallet_switchEthereumChain",
				params: [{chainId: expectedChainId}],
			});
		} catch (switchErr) {
			console.error("[approveUSDT] switch chain error:", switchErr);
			if (isUserRejectedError(switchErr)) {
				return {success: false, error: '用户取消切换网络'};
			}
			throw switchErr;
		}
	}
}

async function getActiveWallet() {
	const [activeWallet] = await provider.request({method: "eth_requestAccounts"});
	return activeWallet
}

async function handleRpcError(error, msg) {
	console.error('购买失败:', error);

	let errorMessage = msg;

	if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
		errorMessage = '用户取消了交易';
	} else if (error.code === -32603) {
		errorMessage = 'Gas费用不足';
	} else if (error.message) {
		errorMessage = error.message;
	}

	return {
		success: false,
		error: errorMessage
	};
}

async function approveUSDT(spenderAddress, amount) {
	if (!provider) return {success: false, error: 'Provider not found'};

	const usdtAddress = isMainnet === true ? USDT_ADDRESS : TEST_USDT_ADDRESS;
	try {
		const activeWallet = await getActiveWallet()
		const data = ERC20_INTERFACE.encodeFunctionData('approve', [spenderAddress, parseUnits(amount, 18)])

		const {
			gas,
			gasPrice,
			nonce,
			maxFeePerGas,
			maxPriorityFeePerGas
		} = await prepareTransactionParams(activeWallet, usdtAddress, data)
		const txParams = {
			from: activeWallet,
			to: usdtAddress,
			data: data,
			gas: gas,
			nonce: nonce,
		};

		if (gasPrice) {
			txParams.gasPrice = gasPrice;
		} else {
			txParams.maxFeePerGas = maxFeePerGas;
			txParams.maxPriorityFeePerGas = maxPriorityFeePerGas;
		}

		const txHash = await provider.request({
			method: 'eth_sendTransaction',  // 实际执行
			params: [txParams]
		});
		console.log('✓ buyNftsWithUSDT执行成功！');
		console.log('交易哈希:', txHash);

		return {
			success: true,
			txHash: txHash
		}
	} catch (error) {
		console.error('购买失败:', error);
		return handleRpcError(error, '购买失败')
	}
}

async function buyNftsWithUSDT(tier, count, referrer, fnNonce, timestamp, deadline, signature) {
	try {
		const activeWallet = await getActiveWallet()
		const data = INTERACTOR_INTERFACE.encodeFunctionData('buyNftsWithUSDT', [tier, count, referrer, fnNonce, timestamp, deadline, signature])
		const result = await provider.request({
			method: 'eth_call',
			params: [{from: activeWallet, to: Interactor, data: data}, 'latest']
		});
		const decoded = INTERACTOR_INTERFACE.decodeFunctionResult('buyNftsWithUSDT', result);
		const tokenIds = decoded[0].map(id => Number(id));  // 第一个返回值
		const {
			gas,
			gasPrice,
			nonce,
			maxFeePerGas,
			maxPriorityFeePerGas
		} = await prepareTransactionParams(activeWallet, Interactor, data)
		const txParams = {
			from: activeWallet,
			to: Interactor,
			data: data,
			gas: gas,
			nonce: nonce,
		};

		if (gasPrice) {
			txParams.gasPrice = gasPrice;
		} else {
			txParams.maxFeePerGas = maxFeePerGas;
			txParams.maxPriorityFeePerGas = maxPriorityFeePerGas;
		}
		const txHash = await provider.request({
			method: 'eth_sendTransaction',  // 实际执行
			params: [txParams]
		});
		console.log('✓ buyNftsWithUSDT执行成功！');
		console.log('交易哈希:', txHash);

		return {
			success: true,
			txHash: txHash,
			tokenIds: tokenIds
		}
	} catch (error) {
		console.error('购买失败:', error);
		return handleRpcError(error, '购买失败')
	}
}

// async function withdrawResonanceDeposit(tokenId, fnNonce, timestamp, deadline, signature) {

// 	try {
// 		const activeWallet = await getActiveWallet()
// 		const data = INTERACTOR_INTERFACE.encodeFunctionData('withdrawNftResonanceDeposit', [tokenId, fnNonce, timestamp, deadline, signature])
//
//
// 		const {
// 			gas,
// 			gasPrice,
// 			nonce,
// 			maxFeePerGas,
// 			maxPriorityFeePerGas
// 		} = await prepareTransactionParams(activeWallet, Interactor, data)
// 		const txParams = {
// 			from: activeWallet,
// 			to: Interactor,
// 			data: data,
// 			gas: gas,
// 			nonce: nonce,
// 		};
//
// 		if (gasPrice) {
// 			txParams.gasPrice = gasPrice;
// 		} else {
// 			txParams.maxFeePerGas = maxFeePerGas;
// 			txParams.maxPriorityFeePerGas = maxPriorityFeePerGas;
// 		}
// 		const txHash = await provider.request({
// 			method: 'eth_sendTransaction',  // 实际执行
// 			params: [txParams]
// 		});
// 		console.log('✓ startResonance执行成功！');
// 		console.log('交易哈希:', txHash);
//
// 		return {
// 			success: true,
// 			txHash: txHash
// 		}
// 	} catch (error) {
// 		console.error('购买失败:', error);
// 		return handleRpcError(error, '购买失败')
// 	}
// }

async function fetchOwnerNFTTokens(ownerAddress) {
	try {
		console.log("Contract Address:", FAANFT);
		console.log("Owner Address:", ownerAddress);

		const contract = new Contract(FAANFT, NFT_ABI, rpcProvider);

		// 添加 staticCall 或确保使用最新区块
		const tokens = await contract.getOwnerTokens(ownerAddress);

		console.log("Owner's tokens:", tokens);
		const tokenIds = tokens.map(id => id.toString());
		console.log("Token IDs:", tokenIds);
		return tokenIds;
	} catch (error) {
		console.error("Error fetching tokens:", error);
		console.error("Error details:", error.message);

		// 如果失败，尝试直接调用
		try {
			const contract = new Contract(FAANFT, NFT_ABI, browserProvider);
			const tokens = await contract.getOwnerTokens.staticCall(ownerAddress);
			return tokens.map(id => id.toString());
		} catch (retryError) {
			console.error("Retry failed:", retryError);
			return [];
		}
	}
}

async function getNFTInfo(tokenId) {
	try {
		console.log("Contract Address:", FAANFT);
		const contract = new Contract(FAANFT, NFT_ABI, rpcProvider);
		const tokenInfo = await contract.getNFTInfo(tokenId);
		console.log("tokenInfo:", tokenInfo);
		return tokenInfo;
	} catch (error) {
		console.error("Error fetching tokens:", error);
	}
}

async function getTierPrice(tier) {
	console.log(`tier: ${tier}`)
	try {
		const contract = new Contract(FAANFT, NFT_ABI, rpcProvider);

		const price = await contract.tierUsdtPrices(tier);
		console.log(`price: ${price}`)
		// return formatUnits(price, 18);
		return price

	} catch (error) {
		console.error("getTierPrice 错误:", error);
		throw error;
	}
}

async function usdtEthCall(method, args) {
	const usdtAddress = isMainnet === true ? USDT_ADDRESS : TEST_USDT_ADDRESS;
	try {
		// 编码 balanceOf 调用
		const data = ERC20_INTERFACE.encodeFunctionData(method, args);

		// RPC 调用
		const result = await provider.request({
			method: 'eth_call',
			params: [{
				to: usdtAddress,
				data: data
			}, 'latest']
		});
		const decoded = ERC20_INTERFACE.decodeFunctionResult(method, result);

		console.log('解码后:', decoded);
		return decoded;

	} catch (error) {
		console.error("nftEthCall 调用错误:", error);
		throw error;
	}
}

// async function nftEthCall( method, args) {
//
// 	try {
// 		// 编码 balanceOf 调用
// 		const data = NFT_INTERFACE.encodeFunctionData(method, args);
//
// 		// RPC 调用
// 		const result = await rpcProvider.request({
// 			method: 'eth_call',
// 			params: [{
// 				to: FAANFT,
// 				data: data
// 			}, 'latest']
// 		});
//
// 		const decoded = NFT_INTERFACE.decodeFunctionResult(method, result);
//
// 		console.log('解码后:', decoded);
// 		return decoded;
//
// 	} catch (error) {
// 		console.error("nftEthCall 调用错误:", error);
// 		throw error;
// 	}
// }

async function nftEthCall(method, args = [], convertToNumber = false) {
	try {
		const argsArray = Array.isArray(args) ? args : [args];

		console.log(`method: ${method}`)
		console.log(argsArray)
		const data = NFT_INTERFACE.encodeFunctionData(method, argsArray);

		const result = await rpcProvider.call({
			to: FAANFT,
			data: data
		});

		const decoded = NFT_INTERFACE.decodeFunctionResult(method, result);

		console.log('解码后:', decoded);

		// 如果需要，转换 BigInt 为普通数字
		if (convertToNumber && decoded.length === 1) {
			return Number(decoded[0]);
		}

		return decoded;

	} catch (error) {
		console.error("nftEthCall 调用错误:", error);
		throw error;
	}
}

async function lpEthCall(method, args = [], convertToNumber = false) {
	try {
		const argsArray = Array.isArray(args) ? args : [args];

		const data = LP_INTERFACE.encodeFunctionData(method, argsArray);

		const result = await rpcProvider.call({
			to: FAALiquidPool,
			data: data
		});

		const decoded = LP_INTERFACE.decodeFunctionResult(method, result);

		console.log('解码后:', decoded);

		// 如果需要，转换 BigInt 为普通数字
		if (convertToNumber && decoded.length === 1) {
			return Number(decoded[0]);
		}

		return decoded;

	} catch (error) {
		console.error("lpEthCall 调用错误:", error);
		throw error;
	}
}

// async function lpEthCall( method, args) {
//
// 	try {
// 		// 编码 balanceOf 调用
// 		const data = LP_INTERFACE.encodeFunctionData(method, args);
//
// 		// RPC 调用
// 		const result = await provider.request({
// 			method: 'eth_call',
// 			params: [{
// 				to: FAALiquidPool,
// 				data: data
// 			}, 'latest']
// 		});
//
// 		const decoded = LP_INTERFACE.decodeFunctionResult(method, result);
//
// 		console.log('解码后:', decoded);
// 		return decoded;
//
// 	} catch (error) {
// 		console.error("lpEthCall 调用错误:", error);
// 		throw error;
// 	}
// }


// async function interactorEthCall( method, args) {
//
// 	try {
// 		// 编码 balanceOf 调用
// 		const data = INTERACTOR_INTERFACE.encodeFunctionData(method, args);
//
// 		// RPC 调用
// 		const result = await provider.request({
// 			method: 'eth_call',
// 			params: [{
// 				to: Interactor,
// 				data: data
// 			}, 'latest']
// 		});
//
// 		const decoded = INTERACTOR_INTERFACE.decodeFunctionResult(method, result);
//
// 		console.log('解码后:', decoded);
// 		return decoded;
//
// 	} catch (error) {
// 		console.error("nftEthCall 调用错误:", error);
// 		throw error;
// 	}
// }

async function interactorEthCall(method, args = [], convertToNumber = false) {
	try {
		const argsArray = Array.isArray(args) ? args : [args];

		const data = INTERACTOR_INTERFACE.encodeFunctionData(method, argsArray);

		const result = await rpcProvider.call({
			to: Interactor,
			data: data
		});

		const decoded = INTERACTOR_INTERFACE.decodeFunctionResult(method, result);

		console.log('解码后:', decoded);

		// 如果需要，转换 BigInt 为普通数字
		if (convertToNumber && decoded.length === 1) {
			return Number(decoded[0]);
		}

		return decoded;

	} catch (error) {
		console.error("interactorEthCall 调用错误:", error);
		throw error;
	}
}

async function startResonance(tokenId, fnNonce, timestamp, deadline, signature) {
	try {
		const activeWallet = await getActiveWallet()
		const data = INTERACTOR_INTERFACE.encodeFunctionData('startResonance', [tokenId, fnNonce, timestamp, deadline, signature])


		const {
			gas,
			gasPrice,
			nonce,
			maxFeePerGas,
			maxPriorityFeePerGas
		} = await prepareTransactionParams(activeWallet, Interactor, data)
		const txParams = {
			from: activeWallet,
			to: Interactor,
			data: data,
			gas: gas,
			nonce: nonce,
		};

		if (gasPrice) {
			txParams.gasPrice = gasPrice;
		} else {
			txParams.maxFeePerGas = maxFeePerGas;
			txParams.maxPriorityFeePerGas = maxPriorityFeePerGas;
		}
		const txHash = await provider.request({
			method: 'eth_sendTransaction',  // 实际执行
			params: [txParams]
		});
		console.log('✓ startResonance执行成功！');
		console.log('交易哈希:', txHash);

		return {
			success: true,
			txHash: txHash
		}
	} catch (error) {
		console.error('购买失败:', error);
		return handleRpcError(error, '购买失败')
	}

}

async function getResonanceRewardMap(user) {
	console.log(`user: ${user}`);
	try {
		const contract = new Contract(Interactor, INTERACTOR_ABI, rpcProvider);
		const info = await contract.resonanceRewardMap(user);
// info 是一个对象（同时也是可索引的数组），包含命名字段
		const totalRewardU = info.totalRewardU;
		const distributedRewardU = info.distributedRewardU;


		console.log(`totalRewardU: ${totalRewardU.toString()}`);
		console.log(`distributedRewardU: ${distributedRewardU.toString()}`);

		return {totalRewardU, distributedRewardU};
	} catch (error) {
		console.error("resonanceRewardMap 调用错误:", error);
		throw error;
	}
}

async function getDirectReferrerRewardMap(user) {
	console.log(`user: ${user}`);
	try {
		const contract = new Contract(Interactor, INTERACTOR_ABI, rpcProvider);
		const info = await contract.directReferrerRewardMap(user);
// info 是一个对象（同时也是可索引的数组），包含命名字段
		const totalRewardU = info.totalRewardU;
		const distributedRewardU = info.distributedRewardU;


		console.log(`totalRewardU: ${totalRewardU.toString()}`);
		console.log(`distributedRewardU: ${distributedRewardU.toString()}`);

		return {totalRewardU, distributedRewardU};
	} catch (error) {
		console.error("getDirectReferrerRewardMap 调用错误:", error);
		throw error;
	}
}

async function getNftResonanceRewardExpireTime() {

	try {
		const contract = new Contract(FAANFT, NFT_ABI, rpcProvider);
		const resonanceRewardExpireTime = await contract.nftResonanceRewardExpireTime();


		console.log(`resonanceRewardExpireTime: ${resonanceRewardExpireTime}`);

		return resonanceRewardExpireTime;
	} catch (error) {
		console.error("getNftResonanceRewardExpireTime 调用错误:", error);
		throw error;
	}
}

async function getNftResonancePeriod() {

	try {
		const contract = new Contract(FAANFT, NFT_ABI, rpcProvider);
		const resonancePeriod = await contract.resonancePeriod();


		console.log(`resonancePeriod: ${resonancePeriod}`);

		return resonancePeriod;
	} catch (error) {
		console.error("getNftResonancePeriod 调用错误:", error);
		throw error;
	}
}

async function getNftExpireTime() {

	try {
		const contract = new Contract(FAANFT, NFT_ABI, rpcProvider);
		const nftExpireTime = await contract.nftExpireTime();

		console.log(`nftExpireTime: ${nftExpireTime}`);

		return nftExpireTime;
	} catch (error) {
		console.error("getNftExpireTime 调用错误:", error);
		throw error;
	}
}

async function getComputingPower(address) {
	try {
		const contract = new Contract(FAALiquidPool, LP_ABI, rpcProvider);
		const computingPower = await contract.computingPowerMap(address);

		console.log(`computingPower: ${computingPower}`);

		return computingPower;
	} catch (error) {
		console.error("getComputingPower 调用错误:", error);
		throw error;
	}
}

async function getPendingFacInLp(address) {
	try {
		const contract = new Contract(FAALiquidPool, LP_ABI, rpcProvider);
		const pendingFacReward = await contract.calculatePendingReward(address);

		console.log(`pendingFacReward: ${pendingFacReward}`);

		return pendingFacReward;
	} catch (error) {
		console.error("getComputingPower 调用错误:", error);
		throw error;
	}
}

async function getTotalComputingPower() {
	try {
		const contract = new Contract(FAALiquidPool, LP_ABI, rpcProvider);
		const totalComputingPower = await contract.totalComputingPower();

		console.log(`totalComputingPower: ${totalComputingPower}`);

		return totalComputingPower;
	} catch (error) {
		console.error("getTotalComputingPower 调用错误:", error);
		throw error;
	}
}

async function getLastDistributionTime() {
	try {
		const contract = new Contract(FAALiquidPool, LP_ABI, rpcProvider);
		const lastDistributionTime = await contract.lastDistributionTime();

		console.log(`lastDistributionTime: ${lastDistributionTime}`);

		return lastDistributionTime;
	} catch (error) {
		console.error("getLastDistributionTime 调用错误:", error);
		throw error;
	}
}

async function getDistributionPeriod() {
	try {
		const contract = new Contract(FAALiquidPool, LP_ABI, rpcProvider);
		const distributionPeriod = await contract.DISTRIBUTION_PERIOD();

		console.log(`distributionPeriod: ${distributionPeriod}`);

		return distributionPeriod;
	} catch (error) {
		console.error("getDistributionPeriod 调用错误:", error);
		throw error;
	}
}

async function getLpLaunchAt() {
	try {
		const contract = new Contract(FAALiquidPool, LP_ABI, rpcProvider);
		const launchAt = await contract.launchAt();

		console.log(`lp launchAt: ${launchAt}`);

		return launchAt;
	} catch (error) {
		console.error("getLpLaunchAt 调用错误:", error);
		throw error;
	}
}

async function getLevelRewardDistributed(address) {
	try {
		const contract = new Contract(Interactor, INTERACTOR_ABI, rpcProvider);
		const distributedLevelReward = await contract.levelDistributedMap(address);

		console.log(`distributedLevelReward: ${distributedLevelReward}`);

		return distributedLevelReward;
	} catch (error) {
		console.error("getLevelRewardDistributed 调用错误:", error);
		throw error;
	}
}

async function sendInteractorTrans (method, args) {
	try {
		const activeWallet = await getActiveWallet()
		const data = INTERACTOR_INTERFACE.encodeFunctionData(method, args)


		const {
			gas,
			gasPrice,
			nonce,
			maxFeePerGas,
			maxPriorityFeePerGas
		} = await prepareTransactionParams(activeWallet, Interactor, data)
		const txParams = {
			from: activeWallet,
			to: Interactor,
			data: data,
			gas: gas,
			nonce: nonce,
		};

		if (gasPrice) {
			txParams.gasPrice = gasPrice;
		} else {
			txParams.maxFeePerGas = maxFeePerGas;
			txParams.maxPriorityFeePerGas = maxPriorityFeePerGas;
		}
		const txHash = await provider.request({
			method: 'eth_sendTransaction',  // 实际执行
			params: [txParams]
		});
		console.log('✓ startResonance执行成功！');
		console.log('交易哈希:', txHash);

		return {
			success: true,
			txHash: txHash
		}
	} catch (error) {
		console.error('购买失败:', error);
		return {success: false, error: error.data.message}
		// return handleRpcError(error, '购买失败')
	}
}

async function sendLpTrans (method, args) {
	try {
		const activeWallet = await getActiveWallet()
		const data = LP_INTERFACE.encodeFunctionData(method, args)


		const {
			gas,
			gasPrice,
			nonce,
			maxFeePerGas,
			maxPriorityFeePerGas
		} = await prepareTransactionParams(activeWallet, FAALiquidPool, data)
		const txParams = {
			from: activeWallet,
			to: FAALiquidPool,
			data: data,
			gas: gas,
			nonce: nonce,
		};

		if (gasPrice) {
			txParams.gasPrice = gasPrice;
		} else {
			txParams.maxFeePerGas = maxFeePerGas;
			txParams.maxPriorityFeePerGas = maxPriorityFeePerGas;
		}
		const txHash = await provider.request({
			method: 'eth_sendTransaction',  // 实际执行
			params: [txParams]
		});
		console.log('✓ startResonance执行成功！');
		console.log('交易哈希:', txHash);

		return {
			success: true,
			txHash: txHash
		}
	} catch (error) {
		console.error('购买失败:', error);
		return handleRpcError(error, '购买失败')
	}
}

async function sendErc20Trans (method, args) {
	try {
		const activeWallet = await getActiveWallet()

		const usdtAddress = isMainnet === true ? USDT_ADDRESS : TEST_USDT_ADDRESS;
		const data = ERC20_INTERFACE.encodeFunctionData(method, args)


		const {
			gas,
			gasPrice,
			nonce,
			maxFeePerGas,
			maxPriorityFeePerGas
		} = await prepareTransactionParams(activeWallet, usdtAddress, data)
		const txParams = {
			from: activeWallet,
			to: usdtAddress,
			data: data,
			gas: gas,
			nonce: nonce,
		};

		if (gasPrice) {
			txParams.gasPrice = gasPrice;
		} else {
			txParams.maxFeePerGas = maxFeePerGas;
			txParams.maxPriorityFeePerGas = maxPriorityFeePerGas;
		}
		const txHash = await provider.request({
			method: 'eth_sendTransaction',  // 实际执行
			params: [txParams]
		});
		console.log('✓ startResonance执行成功！');
		console.log('交易哈希:', txHash);

		return {
			success: true,
			txHash: txHash
		}
	} catch (error) {
		console.error('购买失败:', error);
		return handleRpcError(error, '购买失败')
	}
}

async function claimRewardWithAuth(amount, nonce, timestamp, deadline, tokenId, signature) {
	try {
		const signer = await browserProvider.getSigner();
		const contract = new Contract(FAALiquidPool, LP_ABI, signer);
		const tx = await contract.claimRewardWithAuth(amount, nonce, timestamp, deadline, tokenId, signature);

		console.log('交易已发送，哈希:', tx.hash);
		console.log('等待确认...');

		const receipt = await tx.wait();

		console.log('claimRewardWithAuth成功！');
		console.log('区块号:', receipt.blockNumber);

		return {success: true, tx: tx.hash};
	} catch (error) {
		console.error('claimRewardWithAuth error:', error);

		let errorMessage = '';

		if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
			errorMessage = '用户取消了交易';
		} else if (error.code === -32603) {
			errorMessage = 'Gas费用不足';
		} else if (error.message) {
			errorMessage = error.message;
		}

		return {
			success: false,
			error: errorMessage
		};
	}
}

async function withdrawCommunityReward(amount, tokenId, nonce, timestamp, deadline, signature) {
	try {
		const signer = await browserProvider.getSigner();
		const contract = new Contract(Interactor, INTERACTOR_ABI, signer);
		const tx = await contract.withdrawCommunityReward(amount, tokenId, nonce, timestamp, deadline, signature);

		console.log('交易已发送，哈希:', tx.hash);
		console.log('等待确认...');

		const receipt = await tx.wait();

		console.log('withdrawCommunityReward成功！');
		console.log('区块号:', receipt.blockNumber);

		return {success: true, tx: tx.hash};
	} catch (error) {
		console.error('withdrawCommunityReward error:', error);

		let errorMessage = '';

		if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
			errorMessage = '用户取消了交易';
		} else if (error.code === -32603) {
			errorMessage = 'Gas费用不足';
		} else if (error.message) {
			errorMessage = error.message;
		}

		return {
			success: false,
			error: errorMessage
		};
	}
}

async function withdrawLevelReward(amount, tokenId, nonce, timestamp, deadline, signature) {
	try {
		const signer = await browserProvider.getSigner();
		const contract = new Contract(Interactor, INTERACTOR_ABI, signer);
		const tx = await contract.withdrawLevelReward(amount, tokenId, nonce, timestamp, deadline, signature);

		console.log('交易已发送，哈希:', tx.hash);
		console.log('等待确认...');

		const receipt = await tx.wait();

		console.log('withdrawCommunityReward成功！');
		console.log('区块号:', receipt.blockNumber);

		return {success: true, tx: tx.hash};
	} catch (error) {
		console.error('withdrawLevelReward error:', error);

		let errorMessage = '';

		if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
			errorMessage = '用户取消了交易';
		} else if (error.code === -32603) {
			errorMessage = 'Gas费用不足';
		} else if (error.message) {
			errorMessage = error.message;
		}

		return {
			success: false,
			error: errorMessage
		};
	}
}

async function withdrawNftResonanceReward(amount, tokenId) {
	try {
		const signer = await browserProvider.getSigner();
		const contract = new Contract(Interactor, INTERACTOR_ABI, signer);
		const tx = await contract.withdrawNftResonanceReward(amount, tokenId);

		console.log('交易已发送，哈希:', tx.hash);
		console.log('等待确认...');

		const receipt = await tx.wait();

		console.log('withdrawNftResonanceReward成功！');
		console.log('区块号:', receipt.blockNumber);

		return {success: true, tx: tx.hash};
	} catch (error) {
		console.error('withdrawNftResonanceReward error:', error);

		let errorMessage = '';

		if (error.code === 4001 || error.code === 'ACTION_REJECTED') {
			errorMessage = '用户取消了交易';
		} else if (error.code === -32603) {
			errorMessage = 'Gas费用不足';
		} else if (error.message) {
			errorMessage = error.message;
		}

		return {
			success: false,
			error: errorMessage
		};
	}
}

async function isNftEnergyBurnable(tokenId, amount) {
	console.log(`tokenId: ${tokenId}, amount: ${amount}`)
	const tokenInfo = await getNFTInfo(tokenId)
	const totalEnergy = Number(formatUnits(tokenInfo[3], 18))
	const energyUsed = Number(formatUnits(tokenInfo[4], 18))

	if(totalEnergy - energyUsed < 1) {
		return {isBurnable: false}
	}
	const startResonanceTime = Number(tokenInfo[7])
	const resonanceDepositWithdrawTime = Number(tokenInfo[8])
	const blockTimestamp = Number(await getBlockTimestamp())
	const nftExpireTime = await getNftExpireTime()
	console.log(`totalEnergy: ${totalEnergy}, energyUsed: ${energyUsed}, startResonanceTime: ${startResonanceTime}, resonanceDepositWithdrawTime: ${resonanceDepositWithdrawTime}, nftExpireTime: ${nftExpireTime}`)
	console.log(`totalEnergy - energyUsed: ${totalEnergy - energyUsed}`)
	console.log(`(resonanceDepositWithdrawTime === 0 || blockTimestamp - resonanceDepositWithdrawTime <= nftExpireTime) && startResonanceTime > 0: ${(resonanceDepositWithdrawTime === 0 || blockTimestamp - resonanceDepositWithdrawTime <= nftExpireTime) && startResonanceTime > 0}`)
	if ((resonanceDepositWithdrawTime === 0 || blockTimestamp - resonanceDepositWithdrawTime <= nftExpireTime) && startResonanceTime > 0) {
		if (totalEnergy - energyUsed >= amount) {
			return {isBurnable: true, amount: amount}
		} else {
			return {isBurnable: true, amount: totalEnergy - energyUsed}
		}
	}

	return {isBurnable: false}
	// const isEnergyBurnable = (totalEnergy - energyUsed) >= amount
	// 	&& (resonanceDepositWithdrawTime === 0
	// 		|| blockTimestamp - resonanceDepositWithdrawTime <= nftExpireTime)
	// 	&& startResonanceTime > 0;
	// console.log(`isEnergyBurnable: ${isEnergyBurnable}`)
	// return isEnergyBurnable
}

async function getMyBurnableNftTokenIdForFacToken(wallet, amountInFac) {
	const tokenList = await fetchOwnerNFTTokens(wallet)
	// getUsdtAmountOut
	const amountInU = await lpEthCall('getUsdtAmountOut', [amountInFac])
	console.log(`amountInU: ${Number(formatUnits(amountInU.toString(), 18))}`)
	if (!Array.isArray(tokenList) || tokenList?.length === 0) {
		return {tokenId: null, amount: 0}
	}
	for (const tokenId of tokenList) {
		const {isBurnable, amount} = await isNftEnergyBurnable(tokenId, Number(formatUnits(amountInU.toString(), 18)))
		console.log(`isBurnable:${isBurnable}, amount: ${amount}`)
		if (isBurnable === true) {
			return {tokenId, amount}
		}
	}
	return {tokenId: null, amount: 0}
}


async function getMyBurnableNftTokenId(wallet, amountInUsdt) {
	const tokenList = await fetchOwnerNFTTokens(wallet)
	if (!Array.isArray(tokenList) || tokenList?.length === 0) {
		return {tokenId: null, amount: null}
	}
	for (const tokenId of tokenList) {
		const {isBurnable, amount} = await isNftEnergyBurnable(tokenId, Number(formatUnits(amountInUsdt.toString(), 18)))
		if (isBurnable === true) {
			return {tokenId, amount}
		}
	}
	return {tokenId: null, amount: null}
}

async function getBlockTimestamp() {
	const latestBlock = await rpcProvider.getBlock('latest');
	return latestBlock.timestamp  // 减去缓冲
}

export {
	pickEvmProvider,
	connectDirect,
	sendUsdt,
	maskBscAddress,
	isBscTxHashFormat,
	maskTxHash,
	toChecksumAddress,
	getUsdtBalance,
	checkAllowance,
	approveUSDT,
	buyNftsWithUSDT,
	fetchOwnerNFTTokens,
	getNFTInfo,
	startResonance,
	getTierPrice,
	getResonanceRewardMap,
	getDirectReferrerRewardMap,
	getNftResonanceRewardExpireTime,
	getNftExpireTime,
	getNftResonancePeriod,
	getComputingPower,
	getPendingFacInLp,
	getTotalComputingPower,
	getLastDistributionTime,
	getDistributionPeriod,
	getLpLaunchAt,
	claimRewardWithAuth,
	withdrawCommunityReward,
	withdrawLevelReward,
	withdrawNftResonanceReward,
	isNftEnergyBurnable,
	getLevelRewardDistributed,
	getMyBurnableNftTokenId,
	nftEthCall,
	usdtEthCall,
	lpEthCall,
	interactorEthCall,
	sendInteractorTrans,
	sendLpTrans,
	sendErc20Trans,
	getMyBurnableNftTokenIdForFacToken
}